import DOMPurify from "dompurify";
import dayjs from "dayjs";
import { createEmptySlide, createFallbackSlide, createSlideButton, CTA_DEFAULTS } from "./homepageHero.constants";

export const sanitizeCmsHtml = (value = "") => DOMPurify.sanitize(value, {
  // Preserve the safe formatting generated by the shared Newsletter editor.
  FORBID_TAGS: ["script", "iframe", "object", "embed", "img", "video"],
  FORBID_ATTR: ["onerror", "onload", "onclick"],
});
export const stripHtml = (value = "") => sanitizeCmsHtml(value).replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
export const getPlainTextLength = (value) => stripHtml(value).length;

export const resolveImageUrl = (value = "") => {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//i.test(value)) return value;
  const base = process.env.REACT_APP_IMAGE_URL || "";
  return `${base.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
};

const legacyButton = (cta, order, style) => {
  if (!cta?.label && !cta?.url) return null;
  return createSlideButton(order, {
    ...(cta.id ? { id: cta.id, _isTemporary: false } : {}),
    label: cta.label || "",
    label_nl: cta.label_nl || cta.labelNl || cta.label || "",
    url: cta.url || "",
    textColor: cta.textColor || (order === 1 ? CTA_DEFAULTS.primary.textColor : CTA_DEFAULTS.secondary.textColor),
    backgroundColor: cta.backgroundColor || (order === 1 ? CTA_DEFAULTS.primary.backgroundColor : CTA_DEFAULTS.secondary.backgroundColor),
    borderColor: cta.borderColor || cta.backgroundColor || "#000000",
    style,
    openInNewTab: Boolean(cta.openInNewTab),
  });
};

export const normalizeSlideButtons = (slide = {}) => {
  if (Array.isArray(slide.buttons)) {
    return slide.buttons
      .filter(Boolean)
      .map((button, index) => createSlideButton(index + 1, {
        ...button,
        id: button.id || createSlideButton().id,
        _isTemporary: !button.id,
        style: ["FILLED", "OUTLINE", "TEXT"].includes(button.style) ? button.style : "FILLED",
        order: index + 1,
      }));
  }

  const primary = legacyButton(
    slide.primaryCta || {
      label: slide.primaryButtonLabel,
      url: slide.primaryButtonUrl,
    },
    1,
    "FILLED",
  );
  const secondary = legacyButton(
    slide.secondaryCta || {
      label: slide.secondaryButtonLabel,
      url: slide.secondaryButtonUrl,
    },
    2,
    "OUTLINE",
  );
  return [primary, secondary].filter(Boolean).map((button, index) => ({ ...button, order: index + 1 }));
};

export const serializeSlideButtons = (buttons = []) => buttons
  .map((button, index) => ({
    ...(button.id && !button._isTemporary ? { id: button.id } : {}),
    label: String(button.label || "").trim(),
    label_nl: String(button.label_nl || "").trim(),
    url: String(button.url || "").trim(),
    textColor: button.textColor || "#FFFFFF",
    backgroundColor: button.style === "FILLED" ? button.backgroundColor || "#000000" : "transparent",
    borderColor: button.borderColor || "#000000",
    style: button.style || "FILLED",
    openInNewTab: Boolean(button.openInNewTab),
    order: index + 1,
  }));

export const normalizeHomepageHeroData = (raw) => {
  const content = raw?.content || {};
  if (Array.isArray(content.slides)) {
    const normalizedSlides = content.slides.map((slide, index) => ({
      ...createEmptySlide(index + 1), ...slide, id: slide.id || createEmptySlide().id, order: index + 1,
      // Legacy records may still carry the old "BOTH" value; treat it as "ALL".
      audienceType: (slide.audienceType || slide.audience_type) === "BOTH" ? "ALL" : (slide.audienceType ?? createEmptySlide().audienceType),
      isFallback: Boolean(slide.isFallback ?? slide.is_fallback),
      isActive: Boolean(slide.isFallback ?? slide.is_fallback) ? true : slide.isActive !== false,
      // Only fall back for old records where the Dutch fields do not exist.
      // Preserve an explicitly stored empty value as its own language field.
      title_nl: slide.title_nl ?? slide.title ?? "",
      subtitle_nl: slide.subtitle_nl ?? slide.subtitle ?? "",
      is_permanent: Boolean(slide.isFallback ?? slide.is_fallback) || Boolean(slide.is_permanent ?? slide.isPermanent),
      desktopLayout: slide.desktopLayout || slide.contentPosition || "start",
      tabletLayout: slide.tabletLayout || slide.contentPosition || "start",
      mobileLayout: slide.mobileLayout || slide.contentPosition || "start",
      desktopVerticalLayout: slide.desktopVerticalLayout || "center",
      tabletVerticalLayout: slide.tabletVerticalLayout || "center",
      mobileVerticalLayout: slide.mobileVerticalLayout || "center",
      buttons: normalizeSlideButtons(slide),
    }));
    if (!normalizedSlides.some((slide) => slide.isFallback)) {
      normalizedSlides.push(createFallbackSlide(normalizedSlides.length + 1));
    }
    const regularSlides = normalizedSlides.filter((slide) => !slide.isFallback);
    const fallbackSlide = normalizedSlides.find((slide) => slide.isFallback);
    return { migrated: false, slides: [...regularSlides, fallbackSlide].map((slide, index) => ({ ...slide, order: index + 1 })) };
  }
  if (content.slide1) {
    const legacy = content.slide1;
    const legacySlide = { primaryCta: { label: legacy.button1title || "", url: "/final-sale", ...CTA_DEFAULTS.primary },
      secondaryCta: { label: legacy.button2title || "", url: "", ...CTA_DEFAULTS.secondary } };
    return { migrated: true, slides: [{ ...createEmptySlide(1), title: legacy.heading || "", title_nl: legacy.heading || "", subtitle: legacy.subheading || "", subtitle_nl: legacy.subheading || "",
      buttons: normalizeSlideButtons(legacySlide), desktopImage: legacy.backgroundImage || "",
      contentPosition: content.position || "start", isActive: raw.status !== false }, createFallbackSlide(2)] };
  }
  return { migrated: false, slides: [createFallbackSlide(1)] };
};

export const getSlideScheduleStatus = (slide) => {
  if (slide.isFallback) return "FALLBACK";
  if (!slide.isActive) return "INACTIVE";
  if (slide.is_permanent) return "ACTIVE_NOW";
  const start = dayjs(slide.startDateTime); const end = dayjs(slide.endDateTime); const now = dayjs();
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return "INVALID_SCHEDULE";
  if (now.isBefore(start)) return "UPCOMING";
  if (now.isAfter(end)) return "EXPIRED";
  return "ACTIVE_NOW";
};

export const normalizeForSave = (slides) => slides.map((slide, index) => {
  const buttons = serializeSlideButtons(slide.buttons);
  const [primary, secondary] = buttons;
  return {
    id: slide.id,
    isFallback: Boolean(slide.isFallback),
    is_fallback: Boolean(slide.isFallback),
    title: sanitizeCmsHtml(slide.title),
    title_nl: sanitizeCmsHtml(slide.title_nl),
    subtitle: sanitizeCmsHtml(slide.subtitle),
    subtitle_nl: sanitizeCmsHtml(slide.subtitle_nl),
    audienceType: slide.audienceType,
    buttons,
    // Transitional legacy fields keep older storefront/API deployments working.
    primaryCta: primary ? { label: primary.label, url: primary.url, textColor: primary.textColor, backgroundColor: primary.backgroundColor } : { label: "", url: "", ...CTA_DEFAULTS.primary },
    secondaryCta: secondary ? { label: secondary.label, url: secondary.url, textColor: secondary.textColor, backgroundColor: secondary.backgroundColor } : { label: "", url: "", ...CTA_DEFAULTS.secondary },
    secondaryCtaEnabled: Boolean(secondary),
    desktopImage: slide.desktopImage, tabletImage: slide.tabletImage, mobileImage: slide.mobileImage,
    contentPosition: slide.desktopLayout || slide.contentPosition || "start",
    desktopLayout: slide.desktopLayout || "start",
    tabletLayout: slide.tabletLayout || "start",
    mobileLayout: slide.mobileLayout || "start",
    desktopVerticalLayout: slide.desktopVerticalLayout || "center",
    tabletVerticalLayout: slide.tabletVerticalLayout || "center",
    mobileVerticalLayout: slide.mobileVerticalLayout || "center",
    order: index + 1, isActive: Boolean(slide.isActive), is_permanent: Boolean(slide.is_permanent),
    startDateTime: slide.is_permanent ? null : dayjs(slide.startDateTime).toISOString(),
    endDateTime: slide.is_permanent ? null : dayjs(slide.endDateTime).toISOString(),
  };
});
